import { isObject } from '@iconik-io/core/src/is_object';
import type { DeepPartial } from '@iconik-io/core/src/types';

/**
 * Performs a deep merge of two objects, where the
 * source object is a partial of the target object.
 * Returns a tuple containing the merged result and a partial
 * with all replaced values.
 *
 * The merge logic is very specific to the annotation history
 * and needs to be considered if if is to be used in other contexts.
 * See merge_with_replace_arrays in @iconik-io/core for a more generic merge function.
 *
 * @param target The target object to merge into
 * @param source The source object to merge from
 * @returns A tuple containing [merged result, replaced values]
 */
export function deepMergePartial<T extends Record<string, unknown>>(
  target: T,
  source: DeepPartial<T>,
): [DeepPartial<T>, DeepPartial<T>] {
  const output = structuredClone(target);
  const replaced: Partial<T> = {};

  for (const key in source) {
    const sourceValue = source[key];
    const targetValue = output[key];

    if (isObject(sourceValue) && isObject(targetValue)) {
      const [mergedValue, replacedValue] = deepMergePartial<
        Record<string, unknown>
      >(targetValue, sourceValue);
      // biome-ignore lint/plugin: writing a computed value back into a generic mapped type (output[key]: T[keyof T]) is not verifiable by TypeScript.
      output[key] = mergedValue as T[typeof key];
      if (Object.keys(replacedValue).length > 0) {
        // biome-ignore lint/plugin: writing a computed value back into a generic mapped type (replaced[key]: T[keyof T]) is not verifiable by TypeScript.
        replaced[key] = replacedValue as T[typeof key];
      }
    } else if (sourceValue === undefined) {
      if (key in output) {
        delete output[key];
        // biome-ignore lint/plugin: writing a computed value back into a generic mapped type (replaced[key]: T[keyof T]) is not verifiable by TypeScript.
        replaced[key] = targetValue as T[typeof key];
      }
    } else {
      // biome-ignore lint/plugin: writing a computed value back into a generic mapped type (replaced[key]: T[keyof T]) is not verifiable by TypeScript.
      replaced[key] = targetValue as T[typeof key];
      const nextValue = Array.isArray(sourceValue)
        ? structuredClone(sourceValue)
        : sourceValue;
      // biome-ignore lint/plugin: writing a computed value back into a generic mapped type (output[key]: T[keyof T]) is not verifiable by TypeScript.
      output[key] = nextValue as T[typeof key];
    }
  }

  // biome-ignore lint/plugin: the populated result and replaced map are narrowed to the DeepPartial<T> return contract, which TypeScript cannot infer from the generic T.
  return [output, replaced] as [DeepPartial<T>, DeepPartial<T>];
}
